<script setup lang="ts">
import { useToastStore } from '~/store/pinia/toast'
import Toast from './Toast.vue'

// v2 §Banners & Toast — the single app-level toast mount/stack. client-only +
// Teleport-to-body (toasts are transient overlays, not SSR content). Mounted ONCE
// in app.vue (repo root) — NOT in a layout (the app ships 3 per-page layouts, so a
// layout mount would drop toasts on non-default routes). Stack: top-right desktop /
// top-center mobile (v2 §Feedback & System L840).
defineOptions({ name: 'ToastHost' })
const store = useToastStore()
</script>

<template>
  <client-only>
    <Teleport to="body">
      <div class="dj-toast-host" role="region" aria-label="Notifications">
        <TransitionGroup name="dj-toast">
          <Toast
            v-for="t in store.toasts"
            :key="t.id"
            :id="t.id"
            :variant="t.variant"
            :message="t.message"
            :duration="t.duration"
            @dismiss="store.dismiss"
          />
        </TransitionGroup>
      </div>
    </Teleport>
  </client-only>
</template>

<style scoped lang="scss">
.dj-toast-host {
  position: fixed;
  top: 24px; // v2 §Feedback — top-right on desktop
  right: 24px;
  z-index: 10000; // above all app chrome (header z-999, chat overlays z-9997)
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none; // let clicks pass between toasts

  // v2 — top-center on mobile
  @include phone {
    top: 16px;
    left: 16px;
    right: 16px;
    align-items: center;
  }

  :deep(.dj-toast) {
    pointer-events: auto;
  }
}

// enter/exit — transform + opacity only
.dj-toast-enter-active,
.dj-toast-leave-active {
  transition: transform $dur-base $ease-out, opacity $dur-base $ease-out;
}
.dj-toast-enter-from,
.dj-toast-leave-to {
  transform: translateY(-16px); // slide down from the top edge (top-anchored stack)
  opacity: 0;
}
.dj-toast-leave-active {
  position: absolute;
}

@media (prefers-reduced-motion: reduce) {
  .dj-toast-enter-active,
  .dj-toast-leave-active {
    transition: opacity $dur-fast linear; // reduced-motion → snap (no transform)
  }
  .dj-toast-enter-from,
  .dj-toast-leave-to {
    transform: none;
  }
}
</style>
